<template>
    <div class="agent-project-bar" :class="{ 'expanded': isExpanded, 'has-context': hasProjectContext }">
      <!-- Collapsed View (when no project selected or minimized) -->
      <div v-if="!hasProjectContext" class="no-context-bar" @click="toggleDropdown">
        <i class="fas fa-folder-open"></i>
        <span>Select a project to work in context</span>
        <i class="fas fa-chevron-down dropdown-icon" :class="{ 'rotated': showDropdown }"></i>
      </div>
  
      <!-- Active Context View -->
      <div v-else class="context-bar" @click="toggleDropdown">
        <div class="context-info">
          <div class="project-badge">
            <i class="fas fa-project-diagram"></i>
          </div>
          <div class="project-details">
            <span class="client-name">{{ currentProject?.client_name || 'Client' }}</span>
            <span class="separator">•</span>
            <span class="project-name">{{ currentProject?.project_name || 'Project' }}</span>
            <span v-if="currentProject?.job_title" class="job-title">— {{ currentProject.job_title }}</span>
          </div>
        </div>
        
        <div class="context-actions">
          <span class="switch-hint">
            <i class="fas fa-exchange-alt"></i>
            Switch
          </span>
          <button @click.stop="exitContext" class="exit-btn" title="Exit project context">
            <i class="fas fa-times"></i>
          </button>
        </div>
      </div>
  
      <!-- Projects Dropdown -->
      <transition name="dropdown">
        <div v-if="showDropdown" class="projects-dropdown">
          <div class="dropdown-header">
            <span>Your Projects</span>
            <button @click="closeDropdown" class="close-btn">
              <i class="fas fa-times"></i>
            </button>
          </div>
          
          <div v-if="loadingProjects" class="dropdown-loading">
            <i class="fas fa-spinner fa-spin"></i>
            <span>Loading projects...</span>
          </div>
          
          <div v-else-if="projects.length === 0" class="dropdown-empty">
            <i class="fas fa-inbox"></i>
            <span>No projects assigned</span>
          </div>
          
          <div v-else class="projects-list">
            <div 
              v-for="project in projects" 
              :key="project.project_id"
              :class="['project-item', { 'active': isCurrentProject(project) }]"
              @click="selectProject(project)"
            >
              <div class="project-item-info">
                <span class="item-client">{{ project.client_name }}</span>
                <span class="item-name">{{ project.project_name }}</span>
                <span v-if="project.job_title" class="item-job">{{ project.job_title }}</span>
              </div>
              <div class="project-item-meta">
                <span :class="['status-dot', project.status?.toLowerCase()]"></span>
                <span v-if="project.pending_screening_requests > 0" class="pending-badge">
                  {{ project.pending_screening_requests }} pending
                </span>
              </div>
              <i v-if="isCurrentProject(project)" class="fas fa-check current-check"></i>
            </div>
          </div>
          
          <div class="dropdown-footer">
            <router-link to="/agent/projects" class="view-all-link" @click="closeDropdown">
              <i class="fas fa-th-list"></i>
              View All Projects
            </router-link>
          </div>
        </div>
      </transition>
  
      <!-- Backdrop -->
      <div v-if="showDropdown" class="dropdown-backdrop" @click="closeDropdown"></div>
    </div>
  </template>
  
  <script>
  import axios from 'axios';
  import { useProjectStore } from '@/store/projectContext';
  
  export default {
    name: 'AgentProjectBar',
    
    props: {
      // Current project from parent (HomeView)
      projectContext: {
        type: Object,
        default: null
      }
    },
    
    emits: ['project-selected', 'exit-context'],
    
    setup() {
      const projectStore = useProjectStore();
      return { projectStore };
    },
    
    data() {
      return {
        showDropdown: false,
        projects: [],
        loadingProjects: false,
        isExpanded: false
      };
    },
    
    computed: {
      hasProjectContext() {
        return !!this.projectContext || this.projectStore.hasActiveProject;
      },
      
      currentProject() {
        return this.projectContext || this.projectStore.activeProject;
      }
    },
    
    methods: {
      async toggleDropdown() {
        this.showDropdown = !this.showDropdown;
        
        if (this.showDropdown && this.projects.length === 0) {
          await this.loadProjects();
        }
      },
      
      closeDropdown() {
        this.showDropdown = false;
      },
      
      async loadProjects() {
        this.loadingProjects = true;
        try {
          const response = await axios.get('/api/v1/agent/projects');
          this.projects = response.data || [];
          console.log('📋 AgentProjectBar: Loaded', this.projects.length, 'projects');
        } catch (error) {
          console.error('❌ AgentProjectBar: Failed to load projects:', error);
          this.projects = [];
        } finally {
          this.loadingProjects = false;
        }
      },
      
      isCurrentProject(project) {
        return this.currentProject?.project_id === project.project_id;
      },
      
      async selectProject(project) {
        if (this.isCurrentProject(project)) {
          this.closeDropdown();
          return;
        }
        
        console.log('🔄 AgentProjectBar: Switching to project:', project.project_name);
        
        // Update store
        this.projectStore.setProject({
          project_id: project.project_id,
          project_name: project.project_name,
          job_title: project.job_title,
          client_name: project.client_name,
          status: project.status
        });
        
        // Emit to parent
        this.$emit('project-selected', project);
        
        // Update URL
        this.$router.replace({
          query: {
            ...this.$route.query,
            project_id: project.project_id,
            from: 'agent_dashboard'
          }
        });
        
        this.closeDropdown();
      },
      
      exitContext() {
        console.log('🚪 AgentProjectBar: Exiting project context');
        
        this.projectStore.clearProject();
        this.$emit('exit-context');
        
        // Clear URL params
        this.$router.replace({ query: {} });
        
        this.closeDropdown();
      }
    }
  };
  </script>
  
  <style scoped>
  .agent-project-bar {
    position: relative;
    z-index: 10;
  }
  
  /* No Context Bar */
  .no-context-bar {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    padding: 0.75rem 1.25rem;
    background: var(--surface-color);
    border: 1px dashed var(--border-color);
    border-radius: 0.75rem;
    cursor: pointer;
    transition: all 0.2s ease;
    color: rgba(250, 249, 246, 0.6);
  }
  
  .no-context-bar:hover {
    border-color: var(--primary-color);
    background: rgba(212, 47, 61, 0.05);
    color: var(--text-color);
  }
  
  .no-context-bar .dropdown-icon {
    margin-left: auto;
    transition: transform 0.2s ease;
  }
  
  .no-context-bar .dropdown-icon.rotated {
    transform: rotate(180deg);
  }
  
  /* Active Context Bar */
  .context-bar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0.75rem 1.25rem;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    border-radius: 0.75rem;
    cursor: pointer;
    transition: all 0.2s ease;
    box-shadow: 0 2px 8px rgba(102, 126, 234, 0.3);
  }
  
  .context-bar:hover {
    box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
  }
  
  .context-info {
    display: flex;
    align-items: center;
    gap: 0.75rem;
  }
  
  .project-badge {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 2.25rem;
    height: 2.25rem;
    background: rgba(255, 255, 255, 0.15);
    border-radius: 50%;
    color: white;
  }
  
  .project-details {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    color: white;
    font-size: 0.9rem;
  }
  
  .client-name {
    font-weight: 600;
  }
  
  .separator {
    opacity: 0.5;
  }
  
  .project-name {
    font-weight: 500;
    opacity: 0.95;
  }
  
  .job-title {
    opacity: 0.75;
    font-size: 0.85rem;
  }
  
  .context-actions {
    display: flex;
    align-items: center;
    gap: 1rem;
  }
  
  .switch-hint {
    display: flex;
    align-items: center;
    gap: 0.4rem;
    font-size: 0.8rem;
    color: rgba(255, 255, 255, 0.7);
    padding: 0.35rem 0.75rem;
    background: rgba(255, 255, 255, 0.1);
    border-radius: 1rem;
  }
  
  .exit-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 1.75rem;
    height: 1.75rem;
    background: rgba(255, 255, 255, 0.15);
    border: none;
    border-radius: 50%;
    color: white;
    cursor: pointer;
    transition: all 0.2s ease;
  }
  
  .exit-btn:hover {
    background: rgba(255, 255, 255, 0.25);
  }
  
  /* Dropdown */
  .projects-dropdown {
    position: absolute;
    top: calc(100% + 0.5rem);
    left: 0;
    right: 0;
    background: var(--surface-color);
    border: 1px solid var(--border-color);
    border-radius: 0.75rem;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
    max-height: 400px;
    overflow: hidden;
    display: flex;
    flex-direction: column;
  }
  
  .dropdown-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0.75rem 1rem;
    border-bottom: 1px solid var(--border-color);
    font-weight: 600;
    color: var(--text-color);
  }
  
  .dropdown-header .close-btn {
    background: none;
    border: none;
    color: rgba(250, 249, 246, 0.5);
    cursor: pointer;
    padding: 0.25rem;
  }
  
  .dropdown-header .close-btn:hover {
    color: var(--text-color);
  }
  
  .dropdown-loading,
  .dropdown-empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 2rem;
    color: rgba(250, 249, 246, 0.5);
    gap: 0.5rem;
  }
  
  .projects-list {
    flex: 1;
    overflow-y: auto;
    padding: 0.5rem;
  }
  
  .project-item {
    display: flex;
    align-items: center;
    padding: 0.75rem 1rem;
    border-radius: 0.5rem;
    cursor: pointer;
    transition: all 0.15s ease;
    margin-bottom: 0.25rem;
  }
  
  .project-item:hover {
    background: rgba(250, 249, 246, 0.05);
  }
  
  .project-item.active {
    background: rgba(102, 126, 234, 0.15);
    border: 1px solid rgba(102, 126, 234, 0.3);
  }
  
  .project-item-info {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 0.15rem;
    min-width: 0;
  }
  
  .item-client {
    font-weight: 600;
    font-size: 0.85rem;
    color: var(--text-color);
  }
  
  .item-name {
    font-size: 0.8rem;
    color: rgba(250, 249, 246, 0.7);
  }
  
  .item-job {
    font-size: 0.75rem;
    color: rgba(250, 249, 246, 0.5);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  
  .project-item-meta {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin-right: 0.5rem;
  }
  
  .status-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: #888;
  }
  
  .status-dot.active {
    background: #22c55e;
  }
  
  .status-dot.completed {
    background: #3b82f6;
  }
  
  .status-dot.on_hold {
    background: #f59e0b;
  }
  
  .pending-badge {
    font-size: 0.7rem;
    padding: 0.15rem 0.4rem;
    background: rgba(212, 47, 61, 0.15);
    color: var(--primary-color);
    border-radius: 0.75rem;
    white-space: nowrap;
  }
  
  .current-check {
    color: #667eea;
    font-size: 0.9rem;
  }
  
  .dropdown-footer {
    padding: 0.75rem 1rem;
    border-top: 1px solid var(--border-color);
  }
  
  .view-all-link {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    padding: 0.5rem;
    color: var(--primary-color);
    text-decoration: none;
    font-size: 0.85rem;
    font-weight: 500;
    border-radius: 0.375rem;
    transition: background 0.15s ease;
  }
  
  .view-all-link:hover {
    background: rgba(212, 47, 61, 0.1);
  }
  
  /* Backdrop */
  .dropdown-backdrop {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: -1;
  }
  
  /* Transitions */
  .dropdown-enter-active,
  .dropdown-leave-active {
    transition: all 0.2s ease;
  }
  
  .dropdown-enter-from,
  .dropdown-leave-to {
    opacity: 0;
    transform: translateY(-10px);
  }
  
  /* Light theme */
  :root[data-theme="light"] .no-context-bar {
    color: rgba(42, 42, 40, 0.6);
  }
  
  :root[data-theme="light"] .no-context-bar:hover {
    color: #2a2a28;
  }
  
  :root[data-theme="light"] .dropdown-loading,
  :root[data-theme="light"] .dropdown-empty {
    color: rgba(42, 42, 40, 0.5);
  }
  
  :root[data-theme="light"] .item-name {
    color: rgba(42, 42, 40, 0.7);
  }
  
  :root[data-theme="light"] .item-job {
    color: rgba(42, 42, 40, 0.5);
  }
  
  /* Mobile */
  @media (max-width: 768px) {
    .project-details {
      flex-wrap: wrap;
      gap: 0.25rem;
    }
    
    .job-title {
      display: none;
    }
    
    .switch-hint span {
      display: none;
    }
  }
  </style>